import React, { useState, useEffect, useRef } from "react";
import { FaSearch, FaEye, FaArrowLeft, FaCalendarAlt, FaFolder, FaFolderOpen } from "react-icons/fa";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
// Reuse the Review Existing Goals page styles verbatim so this page matches it
// pixel for pixel; ClientHistory.css only adds the review-history extras.
import "./GoalsPage.css";
import "./ClientHistory.css";
// Single shared implementation so this table's GOAL DOMAIN column can never
// drift from the Review Existing Goals table. Imported from the module that
// owns it rather than re-exported through a page component.
import { buildDomainTags, buildDomainTagsFromGoal } from "./goalDomainTags";
import { statusLabel, statusTone, statusOutcome, OUTCOME_FOLDERS } from "./statusTone";
import { apiUrl } from "../apiConfig";

const API_BASE = `${apiUrl("therapist_api")}/`;

const formatDate = (value) => {
  if (!value) return "";
  const datePart = String(value).split(" ")[0];
  const parts = datePart.split("-");
  if (parts.length !== 3) return value;
  const [year, month, day] = parts;
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  if (year && month && day) return `${parseInt(day)} ${months[parseInt(month) - 1]} ${year}`;
  return value;
};

const sessionLabel = (appointmentType) => {
  const value = String(appointmentType || "");
  if (!value) return "";
  // Canonical labels used across the app - the API returns the raw pathway
  // label ("review smart goal"), which would otherwise read mangled here.
  const map = {
    initial: "Initial",
    review: "Review",
    "review smart goal": "Review Smart Goal",
  };
  const key = value.trim().toLowerCase();
  return map[key] || value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

// Goal domains arrive as two index-aligned arrays (goalDomain / goalSkills) and
// are rendered by the shared tag builder, so a cell reads
// "Motor - Grasp And Release" rather than the bare domain. That builder also
// collapses a domain repeated as its own skill, so "Vision" never shows as
// "Vision-Vision".

/**
 * Flatten sessions x reviews into one table row per SAVED STATE.
 *
 * This is what makes the page an audit trail:
 *   - the original Intervention Planning goal ("Goal Set", reviewId 0)
 *   - one row per Goal Review, newest last
 *
 * Do NOT collapse this into one row per session. That was tried and reverted: it
 * made the first-time row disappear, because the single row had to show the
 * LATEST review, and then every Edit / Update on /goalreview overwrote what the
 * therapist originally saved instead of adding to it. Therapists need the
 * original Goal Set row to stay exactly as it was, with each review appearing as
 * a new row beneath it.
 *
 * The session's own values are used for the base row only; every review row
 * takes its SMART GOAL / TARGET / STATUS / DOMAIN from that specific review, so
 * a row is a faithful snapshot of what was saved at that moment. That is why
 * get_client_history.php returns the PLANNING goal's smart_goal / target_date at
 * session level - it is what the base row must show, and overriding it with the
 * newest review would rewrite history.
 */
const buildHistoryRows = (sessions) => {
  const out = [];

  (Array.isArray(sessions) ? sessions : []).forEach((s) => {
    if (!s) return;

    const base = {
      client: s.client,
      clientId: s.clientId,
      clientRef: s.clientRef,
      clientCode: s.clientCode,
      appointmentType: s.appointmentType,
      ageGroup: s.ageGroup,
      sessionId: s.sessionId,
      isDraft: s.isDraft,
      summaryConfirmed: s.summaryConfirmed,
      answerCount: s.answerCount,
      // Every row this builder emits exists only because goals were saved, so
      // the "view goals" eye is always enabled here. Carried explicitly because
      // the session-level flag used to come straight off the API payload.
      hasGoals: true,
    };

    // --- Row 1: the original Goal Set (reviewId 0) -------------------------
    // Only shown when the session actually has planning goals, so a session that
    // was never planned does not produce a misleading empty row. This row is
    // immutable - it is the plan as it was first saved.
    if (s.hasGoals) {
      out.push({
        ...base,
        rowKey: `${s.sessionId}-base`,
        isBaseRow: true,
        reviewId: 0,
        reviewNo: 0,
        rowLabel: "Goal Set",
        assessmentDate: s.assessmentDate,
        lastReviewDate: s.lastReviewDate,
        smartGoal: s.smartGoal,
        goalDomainTags: buildDomainTags({
          goalDomain: s.goalDomain,
          goalSkills: s.goalSkills,
        }),
        targetDate: s.targetDate,
        status: s.status,
        achievedBy: s.achievedBy,
        isClosed: s.isClosed,
        comments: s.comments,
      });
    }

    // --- One row per saved Goal Review ------------------------------------
    const reviews = Array.isArray(s.reviews) ? s.reviews : [];
    reviews.forEach((rv) => {
      const goals = Array.isArray(rv.goals) ? rv.goals : [];
      const g = goals[0] || {};
      out.push({
        ...base,
        rowKey: `${s.sessionId}-r${rv.reviewId}`,
        isBaseRow: false,
        reviewId: rv.reviewId,
        reviewNo: rv.reviewNo,
        rowLabel: `Review ${rv.reviewNo}`,
        // The review is what dated this state, so it is the ASSESSED date for
        // that row; fall back to the session date if it is somehow missing.
        assessmentDate: rv.reviewCreatedAt || s.assessmentDate,
        lastReviewDate: rv.reviewCreatedAt || s.lastReviewDate,
        smartGoal: g.smart_goal ?? "",
        goalDomainTags: buildDomainTagsFromGoal(g),
        targetDate: g.target_date || "",
        // Review rows can carry the placeholder `Select` too. The endpoints now
        // resolve that to "In progress - on track" themselves
        // (therapist_api/services/goal_status.php), so the value passes straight
        // through; this only guards a row that still arrives blank.
        status: g.status || "",
        achievedBy: g.result_date || "",
        isClosed: Number(g.is_closed) === 1 ? 1 : 0,
        comments: g.outcome_measure || "",
      });
    });
  });

  // Newest first, matching the Review Existing Goals page.
  return out.sort((a, b) => new Date(b.assessmentDate || 0) - new Date(a.assessmentDate || 0));
};

const ClientHistory = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const clientId = searchParams.get("clientId") || "";
  const focusSessionId = searchParams.get("sessionId") || "";

  const [client, setClient] = useState(null);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Same filter / sort / pagination behaviour as the Review Existing Goals page.
  const [globalSearch, setGlobalSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);

  // Outcome folder: null = show every row, otherwise only the rows of that one
  // folder ("Achieved" / "Not achieved by target date"). Independent of the
  // search box and the date range, so all three narrow the same list.
  const [outcomeFolder, setOutcomeFolder] = useState(null);
  const rowsPerPage = 10;
  const fromDateRef = useRef(null);
  const toDateRef = useRef(null);

  // Every saved state is its own table row (the original Goal Set, then one row
  // per Review), so there is no separate review-history accordion to expand any
  // more - the rows themselves are the history.

  const token = localStorage.getItem("token");

  const loadHistory = async () => {
    if (!clientId) {
      setLoading(false);
      setError("No client was selected.");
      return;
    }
    if (!token) {
      setLoading(false);
      setError("Session expired. Please sign in again.");
      return;
    }
    try {
      setLoading(true);
      setError("");
      const response = await fetch(`${API_BASE}get_client_history.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ client_id: parseInt(clientId) }),
      });
      const json = await response.json();
      if (json.status) {
        setClient(json.client || null);
        setData(buildHistoryRows(json.sessions));
      } else {
        setData([]);
        setError(json.message || "Unable to load the client history.");
      }
    } catch (err) {
      setData([]);
      setError(err.message || "Network error while loading the client history.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, token]);

  const handleSort = (column) => {
    let direction = "asc";
    if (sortConfig.key === column && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key: column, direction });
  };

  useEffect(() => { setCurrentPage(1); }, [globalSearch, fromDate, toDate, outcomeFolder]);

  // Filtering - identical semantics to the Review Existing Goals page.
  // Done in two steps so the folder counts can be taken BEFORE the folder
  // itself is applied: the number on a folder has to be what clicking it would
  // actually show, otherwise the badge lies as soon as a folder is active.
  const searchAndDateFiltered = data.filter((row) => {
    const q = globalSearch.toLowerCase();
    const matchesGlobal =
      String(row.clientId ?? "").toLowerCase().includes(q) ||
      String(row.client ?? "").toLowerCase().includes(q) ||
      String(row.assessmentDate ?? "").toLowerCase().includes(q) ||
      String(row.appointmentType ?? "").toLowerCase().includes(q) ||
      String(row.rowLabel ?? "").toLowerCase().includes(q) ||
      String(row.smartGoal ?? "").toLowerCase().includes(q) ||
      // Match the rendered "Domain - Skill" tags, so searching for either half
      // finds the row - searching the skill alone used to return nothing.
      (row.goalDomainTags || []).join(" ").toLowerCase().includes(q) ||
      String(row.targetDate ?? "").toLowerCase().includes(q) ||
      String(row.lastReviewDate ?? "").toLowerCase().includes(q) ||
      String(row.status ?? "").toLowerCase().includes(q);

    let rowDate = null;
    if (row.assessmentDate) {
      rowDate = new Date(String(row.assessmentDate).split(" ")[0]);
    }

    const from = fromDate ? new Date(fromDate) : new Date("1900-01-01");
    const to = toDate ? new Date(toDate) : new Date("2100-12-31");
    if (toDate) to.setHours(23, 59, 59, 999);

    const matchesDateRange =
      rowDate && !isNaN(rowDate.getTime())
        ? rowDate >= from && rowDate <= to
        : true;

    return matchesGlobal && matchesDateRange;
  });

  // How many rows each folder holds under the current search + date range.
  const outcomeCounts = searchAndDateFiltered.reduce((acc, row) => {
    const outcome = statusOutcome(row.status);
    if (outcome) acc[outcome] = (acc[outcome] || 0) + 1;
    return acc;
  }, {});

  // Rows whose goal is still in progress belong to no folder, so they drop out
  // as soon as one is picked.
  let filteredData =
    outcomeFolder == null
      ? searchAndDateFiltered
      : searchAndDateFiltered.filter(
          (row) => statusOutcome(row.status) === outcomeFolder
        );

  if (sortConfig.key) {
    filteredData = [...filteredData].sort((a, b) => {
      // GOAL DOMAIN lives in an array of rendered tags, so read the sort value
      // from there; every other column is a plain scalar.
      const read = (row) => {
        const v = row[sortConfig.key];
        if (Array.isArray(v)) return v.join(", ");
        return v == null ? "" : v;
      };
      const x = read(a);
      const y = read(b);
      if (x < y) return sortConfig.direction === "asc" ? -1 : 1;
      if (x > y) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }

  const totalPages = Math.max(1, Math.ceil(filteredData.length / rowsPerPage));
  const paginatedData = filteredData.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const goToPage = (page) => { if (page >= 1 && page <= totalPages) setCurrentPage(page); };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const subtitle = client
    ? `${client.clientName}${client.clientCode ? ` | ID: ${client.clientCode}` : ""}${client.ageGroup ? ` | Age group: ${client.ageGroup}` : ""}`
    : "Track caregiver led goals here";

  return (
    <>
      <Link onClick={() => navigate(-1)} className="back-btn2" aria-label="Go back to dashboard">
        <FaArrowLeft className="back-icon" aria-hidden="true" />
        Previous page
      </Link>
      <div className="continue-container">
        <div className="common-page-header">
          <h1 className="common-page-title">
            {client ? `${client.clientName} - Goal Review Records` : "Goal Review Records"}
          </h1>
          <p className="common-page-subtitle">{subtitle}</p>
        </div>

        <div className="filter-section">
          <div className="search-box">
            <div className="common-search-box">
              <input
                type="text"
                placeholder="Search"
                value={globalSearch}
                onChange={(e) => setGlobalSearch(e.target.value)}
                aria-label="Search this client's history"
              />
              <FaSearch className="common-search-icon" />
            </div>
          </div>

          <div className="filter-outcome-group">
            {/*
              Two outcome folders, to the left of the From / To range. Tapping
              the active folder clears it and brings every row back, so this is
              a filter rather than a navigation. Each button carries how many
              rows it would show.
            */}
            {/* <div className="filter-folders" role="group" aria-label="Filter by outcome">
              {OUTCOME_FOLDERS.map(({ id, label }) => {
                const isActive = outcomeFolder === id;
                return (
                  <div className="filter-folder" key={id}>
                    <span className="filter-folder-label">{label}</span>
                    <button
                      type="button"
                      className={`filter-folder-box filter-folder-box--${id}${isActive ? " filter-folder-box--active" : ""}`}
                      aria-pressed={isActive}
                      aria-label={`${label}: ${outcomeCounts[id] || 0} rows`}
                      onClick={() => setOutcomeFolder(isActive ? null : id)}
                    >
                     
                      {isActive ? (
                        <FaFolderOpen className="filter-folder-icon" aria-hidden="true" />
                      ) : (
                        <FaFolder className="filter-folder-icon" aria-hidden="true" />
                      )}
                      <span className="filter-folder-count">{outcomeCounts[id] || 0}</span>
                    </button>
                  </div>
                );
              })}
            </div> */}

            <div className="filter-date-range">
            <div className="filter-date-wrapper">
              <label htmlFor="history-from-date" className="sr-only">From Date</label>
              <div className="filter-date-box" role="button" tabIndex="0" onClick={() => fromDateRef.current?.showPicker()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fromDateRef.current?.showPicker(); } }} aria-label={`Filter from date${fromDate ? `: ${fromDate}` : ""}`} aria-haspopup="dialog">
                <span className={!fromDate ? "filter-date-placeholder" : "filter-date-value"}>
                  {fromDate || "From"}
                </span>
                <FaCalendarAlt className="filter-date-icon" />
                <input
                  type="date"
                  id="history-from-date"
                  name="fromDate"
                  ref={fromDateRef}
                  value={fromDate}
                  onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }}
                  className="filter-date-input"
                  aria-label="From date"
                />
              </div>
            </div>

            <div className="filter-date-wrapper">
              <label htmlFor="history-to-date" className="sr-only">To Date</label>
              <div className="filter-date-box" role="button" tabIndex="0" onClick={() => toDateRef.current?.showPicker()} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toDateRef.current?.showPicker(); } }} aria-label={`Filter to date${toDate ? `: ${toDate}` : ""}`} aria-haspopup="dialog">
                <span className={!toDate ? "filter-date-placeholder" : "filter-date-value"}>
                  {toDate || "To"}
                </span>
                <FaCalendarAlt className="filter-date-icon" />
                <input
                  type="date"
                  id="history-to-date"
                  name="toDate"
                  ref={toDateRef}
                  value={toDate}
                  onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }}
                  className="filter-date-input"
                  aria-label="To date"
                />
              </div>
            </div>
          </div>
          </div>
        </div>

        <div className="table-wrapper gotable-wrapper">
          {loading ? (
            <div style={{ textAlign: "center", padding: "20px" }} role="status" aria-live="polite">
              Loading client history...
            </div>
          ) : error ? (
            <div style={{ textAlign: "center", padding: "20px" }} role="alert">{error}</div>
          ) : (
            <table className="goals-table" style={{ marginBottom: "20px", tableLayout: "fixed" }}>
              <caption className="sr-only">Client history data</caption>
              <colgroup>
                {/* STATUS takes the room it needs from GOAL DOMAIN, which is
                    clamped to two lines and wraps by design, rather than
                    pushing the badge over the ACTIONS column. */}
                <col style={{ width: "14%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "14%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "7%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "12%" }} />
              </colgroup>
              <thead>
                <tr>
                  {[
                    { key: "client", label: "CLIENT" },
                    { key: "assessmentDate", label: "ASSESSED" },
                    { key: "smartGoal", label: "SMART GOAL" },
                    { key: "goalDomainTags", label: "GOAL DOMAIN" },
                    { key: "appointmentType", label: "SESSION" },
                    { key: "targetDate", label: "TARGET" },
                    { key: "lastReviewDate", label: "LAST REVIEW" },
                    { key: "status", label: "STATUS" },
                  ].map(({ key, label }) => (
                    <th
                      key={key}
                      scope="col"
                      onClick={() => handleSort(key)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSort(key);
                        }
                      }}
                      tabIndex="0"
                      aria-label={`Sort by ${label}${sortConfig?.key === key ? `, current order ${sortConfig.direction === "asc" ? "ascending" : "descending"}` : ""}`}
                      aria-sort={sortConfig?.key === key ? (sortConfig.direction === "asc" ? "ascending" : "descending") : "none"}
                    >
                      {label}
                    </th>
                  ))}
                  <th scope="col">ACTIONS</th>
                </tr>
              </thead>

              <tbody>
                {paginatedData.map((row, index) => (
                  <React.Fragment key={row.rowKey || row.sessionId}>
                    <tr
                      className={[
                        String(row.sessionId) === String(focusSessionId) ? "ch-row-focused" : "",
                        row.isBaseRow ? "ch-row-base" : "ch-row-review",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                    >
                      <td className="goals-td-client">
                        <span className="goals-client-name">{row.client}</span>
                        <span className="goals-client-id">{row.clientRef}</span>
                      </td>

                      <td className="goals-td-center">{formatDate(row.assessmentDate)}</td>

                      <td className="goals-td-left">{row.smartGoal}</td>

                      <td className="goals-td-domain">
                        {(row.goalDomainTags || []).map((tag, i) => (
                          <span key={i} className="goals-domain-tag">
                            {tag}
                          </span>
                        ))}
                      </td>

                      <td className="goals-td-center">{sessionLabel(row.appointmentType)}</td>

                      <td className="goals-td-center">{formatDate(row.targetDate)}</td>

                      <td className="goals-td-center">{formatDate(row.lastReviewDate)}</td>

                      <td className="goals-td-status">
                        {/*
                          One badge for the whole status, matching the Review
                          Existing Goals table: a status is a single value and
                          has to be colour-coded as one, so it is no longer split
                          into one uncoloured chip per "-" fragment.
                        */}
                        <span className={`goals-status-badge goals-status-badge--${statusTone(row.status)}`}>
                          {statusLabel(row.status)}
                        </span>
                      </td>

                      <td className="goals-td-actions">
                        <div className="goals-actions-row">
                          {/*
                            Review opens the read-only history page for THIS
                            row, not the editable review page. reviewId 0 is
                            the original Goal Set row; every other row carries
                            the id of the exact review version it came from, so
                            older rows still show the values they had.
                          */}
                          <Link
                            to={`/goalreview-history?sessionId=${row.sessionId}&reviewId=${row.isBaseRow ? 0 : row.reviewId}`}
                            className="goals-action-review"
                            aria-label={`View the goal review for ${row.client} - ${row.rowLabel || "this version"}`}
                          >
                            Review
                          </Link>

                          {row.hasGoals ? (
                            <Link
                              to={`/goalsetdownload?sessionId=${row.sessionId}`}
                              className="goals-action-icon"
                              aria-label={`View goals for ${row.client}`}
                            >
                              <FaEye className="goals-eye-icon" />
                            </Link>
                          ) : (
                            <span className="goals-action-icon goals-action-disabled">
                              <FaEye className="goals-eye-icon" />
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          )}

          {!loading && !error && (
            <nav className="gopagination" aria-label="Pagination">
              <button aria-label="Previous page" disabled={currentPage === 1} onClick={() => goToPage(currentPage - 1)}>Previous</button>
              {getPageNumbers().map((p) => (
                <button
                  key={p}
                  className={p === currentPage ? 'active' : ''}
                  aria-current={p === currentPage ? "page" : undefined}
                  aria-label={`Page ${p}`}
                  onClick={() => goToPage(p)}
                >
                  {p}
                </button>
              ))}
              <button aria-label="Next page" disabled={currentPage === totalPages} onClick={() => goToPage(currentPage + 1)}>Next</button>
            </nav>
          )}
        </div>
      </div>
    </>
  );
};

export default ClientHistory;
